import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { Edit, Trash2 } from 'lucide-react';
import Pagelayout from "../../components/Pagelayout";
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import { HEADERS } from '../../../../../API/UrlAndPaths';
import Swal from 'sweetalert2';

const LeaveType = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [isModify, setIsModify] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    _id: null,
    leaveType: '',
    code: '',
    days: 0,  // Legacy field name
    category: 0,  // Legacy field: 0=paid, 1=unpaid
    isPaid: true,
    isHalfDayAllowed: true,  // Not in backend schema but used in UI
    requiresApproval: true,   // Not in backend schema but used in UI
    maxDaysPerYear: 0,
    maxConsecutiveDays: 0,
    minGapBetweenLeaves: 0,
    canCarryForward: false,
    maxCarryForward: 0,
    canEncash: false,
    minServiceMonths: 0,
    requiresDocument: false,
    applicableGender: 'All',
    accrualType: 'Yearly',
    accrualRate: 0,
    isActive: true,
  });

  const handleBackBtn = () => {
    navigate("/userdashboard/payroll/attendance/payrollLeave");
  };

  // Fetch leave types
  useEffect(() => {
    fetchLeaveTypes();
  }, [isModify]);

  const fetchLeaveTypes = async () => {
    setLoading(true);
    try {
      // Add timestamp to prevent caching
      const res = await axios.get(`employee/viewLeaveType?t=${Date.now()}`, HEADERS);
      console.log('Fetch Leave Types Response:', res);
      console.log('Leave Types Data:', res.data);
      if (res.status === 200) {
        const leaveTypesData = res.data.data || res.data || [];
        console.log('Setting leave types:', leaveTypesData);
        setLeaveTypes(leaveTypesData);
      }
    } catch (error) {
      console.error('Error fetching leave types:', error);
      Swal.fire('Error', 'Failed to fetch leave types', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (leaveType = null) => {
    if (leaveType) {
      setEditMode(true);
      setFormData({
        _id: leaveType._id,
        leaveType: leaveType.leaveType || '',
        code: leaveType.code || '',
        days: leaveType.days || 0,
        category: leaveType.category !== undefined ? leaveType.category : (leaveType.isPaid ? 0 : 1),
        isPaid: leaveType.isPaid !== undefined ? leaveType.isPaid : (leaveType.category === 0),
        isHalfDayAllowed: leaveType.isHalfDayAllowed !== undefined ? leaveType.isHalfDayAllowed : true,
        requiresApproval: leaveType.requiresApproval !== undefined ? leaveType.requiresApproval : true,
        maxDaysPerYear: leaveType.maxDaysPerYear || leaveType.days || 0,
        maxConsecutiveDays: leaveType.maxConsecutiveDays || 0,
        minGapBetweenLeaves: leaveType.minGapBetweenLeaves || 0,
        canCarryForward: leaveType.canCarryForward !== undefined ? leaveType.canCarryForward : false,
        maxCarryForward: leaveType.maxCarryForward || 0,
        canEncash: leaveType.canEncash !== undefined ? leaveType.canEncash : false,
        minServiceMonths: leaveType.minServiceMonths || 0,
        requiresDocument: leaveType.requiresDocument !== undefined ? leaveType.requiresDocument : false,
        applicableGender: leaveType.applicableGender || 'All',
        accrualType: leaveType.accrualType || 'Yearly',
        accrualRate: leaveType.accrualRate || 0,
        isActive: leaveType.isActive !== undefined ? leaveType.isActive : true,
      });
    } else {
      setEditMode(false);
      setFormData({
        _id: null,
        leaveType: '',
        code: '',
        days: 0,
        category: 0,
        isPaid: true,
        isHalfDayAllowed: true,
        requiresApproval: true,
        maxDaysPerYear: 0,
        maxConsecutiveDays: 0,
        minGapBetweenLeaves: 0,
        canCarryForward: false,
        maxCarryForward: 0,
        canEncash: false,
        minServiceMonths: 0,
        requiresDocument: false,
        applicableGender: 'All',
        accrualType: 'Yearly',
        accrualRate: 0,
        isActive: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.leaveType.trim()) {
      Swal.fire('Error', 'Please enter leave type name', 'error');
      return;
    }
    if (!formData.code.trim()) {
      Swal.fire('Error', 'Please enter leave type code', 'error');
      return;
    }
    if (formData.days <= 0) {
      Swal.fire('Error', 'Number of days must be greater than 0', 'error');
      return;
    }

    setLoading(true);
    try {
      // Sync legacy fields with new fields
      const submitData = {
        ...formData,
        category: formData.isPaid ? 0 : 1,  // Sync category with isPaid
        maxDaysPerYear: formData.days,  // Sync maxDaysPerYear with days
      };
      
      const endpoint = editMode ? 'employee/editLeaveType' : 'employee/addLeaveType';
      console.log('Submitting to:', endpoint);
      console.log('Submit Data:', submitData);
      const res = await axios.post(endpoint, submitData, HEADERS);
      
      console.log('Response:', res);
      console.log('Response Data:', res.data);
      
      if (res.status === 200 || res.status === 201 || res.data?.status === 200) {
        setLoading(false);
        handleCloseDialog();
        
        // Refresh the list
        await fetchLeaveTypes();
        
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: `Leave type ${editMode ? 'updated' : 'created'} successfully`,
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      console.error('Error saving leave type:', error);
      setLoading(false);
      const errorMsg = error.response?.data?.msg || error.response?.data?.message || 'Failed to save leave type';
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMsg,
      });
    }
  };

  const handleDelete = async (leaveType) => {
    const result = await Swal.fire({
      title: 'Delete Leave Type?',
      text: `Are you sure you want to delete "${leaveType.leaveType}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      try {
        const res = await axios.post('employee/deleteLeaveType', { _id: leaveType._id }, HEADERS);
        if (res.status === 200) {
          Swal.fire('Deleted!', 'Leave type has been deleted.', 'success');
          setIsModify(!isModify);
        }
      } catch (error) {
        console.error('Error deleting leave type:', error);
        Swal.fire('Error', 'Failed to delete leave type', 'error');
      }
    }
  };

  return (
    <Pagelayout
      pageName="Leave Types"
      headerLayout={2}
      handleBack={handleBackBtn}
      actionBtn={false}
    >
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h5">Manage Leave Types</Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleOpenDialog()}
          >
            Add Leave Type
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Leave Type</strong></TableCell>
                <TableCell><strong>Code</strong></TableCell>
                <TableCell align="center"><strong>Days</strong></TableCell>
                <TableCell align="center"><strong>Type</strong></TableCell>
                <TableCell align="center"><strong>Carry Forward</strong></TableCell>
                <TableCell align="center"><strong>Encashable</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">Loading...</TableCell>
                </TableRow>
              ) : leaveTypes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No leave types found. Click "Add Leave Type" to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                leaveTypes.map((leaveType) => (
                  <TableRow key={leaveType._id}>
                    <TableCell>{leaveType.leaveType}</TableCell>
                    <TableCell>{leaveType.code}</TableCell>
                    <TableCell align="center">{leaveType.days}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={leaveType.isPaid !== undefined ? (leaveType.isPaid ? 'Paid' : 'Unpaid') : (leaveType.category === 0 ? 'Paid' : 'Unpaid')}
                        size="small"
                        color={(leaveType.isPaid !== undefined ? leaveType.isPaid : leaveType.category === 0) ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {leaveType.canCarryForward ? (
                        <Chip label={`Max ${leaveType.maxCarryForward}`} size="small" color="info" />
                      ) : (
                        <Chip label="No" size="small" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {leaveType.canEncash ? (
                        <Chip label={`Max ${leaveType.maxCarryForward}`} size="small" color="warning" />
                      ) : (
                        <Chip label="No" size="small" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={leaveType.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={leaveType.isActive ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenDialog(leaveType)}
                        title="Edit Leave Type"
                      >
                        <Edit size={18} />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(leaveType)}
                        title="Delete Leave Type"
                      >
                        <Trash2 size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>{editMode ? 'Edit Leave Type' : 'Add Leave Type'}</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Leave Type Name"
                    value={formData.leaveType}
                    onChange={(e) => setFormData({ ...formData, leaveType: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Number of Days"
                    value={formData.days}
                    onChange={(e) => setFormData({ ...formData, days: Number(e.target.value) })}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Min Service Months"
                    value={formData.minServiceMonths}
                    onChange={(e) => setFormData({ ...formData, minServiceMonths: Number(e.target.value) })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isPaid}
                        onChange={(e) => setFormData({ ...formData, isPaid: e.target.checked })}
                      />
                    }
                    label="Paid Leave"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isHalfDayAllowed}
                        onChange={(e) => setFormData({ ...formData, isHalfDayAllowed: e.target.checked })}
                      />
                    }
                    label="Half Day Allowed"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.requiresApproval}
                        onChange={(e) => setFormData({ ...formData, requiresApproval: e.target.checked })}
                      />
                    }
                    label="Requires Approval"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      />
                    }
                    label="Active"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.canCarryForward}
                        onChange={(e) => setFormData({ ...formData, canCarryForward: e.target.checked })}
                      />
                    }
                    label="Carry Forward"
                  />
                </Grid>
                {formData.canCarryForward && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Max Carry Forward Days"
                      value={formData.maxCarryForward}
                      onChange={(e) => setFormData({ ...formData, maxCarryForward: Number(e.target.value) })}
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.canEncash}
                        onChange={(e) => setFormData({ ...formData, canEncash: e.target.checked })}
                      />
                    }
                    label="Encashable"
                  />
                </Grid>
                {formData.canEncash && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Max Encashment Days"
                      value={formData.maxCarryForward}
                      onChange={(e) => setFormData({ ...formData, maxCarryForward: Number(e.target.value) })}
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Consecutive Days"
                    value={formData.maxConsecutiveDays}
                    onChange={(e) => setFormData({ ...formData, maxConsecutiveDays: Number(e.target.value) })}
                    helperText="0 = No limit"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Min Gap Between Leaves (days)"
                    value={formData.minGapBetweenLeaves}
                    onChange={(e) => setFormData({ ...formData, minGapBetweenLeaves: Number(e.target.value) })}
                    helperText="0 = No gap required"
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained" color="primary" disabled={loading}>
              {loading ? 'Saving...' : editMode ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Pagelayout>
  );
};

export default LeaveType;
